import React, { FC } from 'react';
import type { WebWorkMixResponse } from './types';
import { fmtInt, fmtShare } from './format';

/**
 * Classification and site coverage for the window, plus the glossary the
 * server ships with the payload so the UI and the numbers can't drift apart.
 */

export interface CoveragePanelProps {
  coverage: WebWorkMixResponse['coverage'];
  definitions: WebWorkMixResponse['definitions'];
}

export const CoveragePanel: FC<CoveragePanelProps> = ({
  coverage,
  definitions,
}) => {
  const classifiedDenominator = coverage.totalRows;
  const domainDenominator = coverage.classifiedRows;
  return (
    <section className='wwm-section' data-testid='coverage-panel'>
      <h3>Coverage</h3>
      <div className='wwm-tiles'>
        <div className='wwm-tile'>
          <div className='k'>Classified</div>
          <div className='v'>
            {fmtInt(coverage.classifiedRows)}{' '}
            <span className='wwm-muted'>/ {fmtInt(coverage.totalRows)}</span>
          </div>
          <div className='d'>
            {fmtShare(coverage.classifiedRows, classifiedDenominator)} of human
            web work in the window
          </div>
        </div>
        <div className='wwm-tile'>
          <div className='k'>Pending classification</div>
          <div className='v'>{fmtInt(coverage.pendingRows)}</div>
          <div className='d'>
            {coverage.pendingRows > 0
              ? 'awaiting the nightly classifier — not in the charts yet'
              : 'everything in the window is classified'}
          </div>
        </div>
        <div className='wwm-tile'>
          <div className='k'>Site known</div>
          <div className='v'>{fmtInt(coverage.domainKnown)}</div>
          <div className='d'>
            {fmtShare(coverage.domainKnown, domainDenominator)} of classified ·{' '}
            {fmtInt(coverage.domainInferredFromSibling)} inferred from a sibling
            web action
          </div>
        </div>
        <div className='wwm-tile'>
          <div className='k'>Site unknown</div>
          <div className='v'>{fmtInt(coverage.domainUnknown)}</div>
          <div className='d'>
            {fmtShare(coverage.domainUnknown, domainDenominator)} of classified
          </div>
        </div>
      </div>

      <h3 id='wwm-definitions'>Definitions</h3>
      <dl className='scaling-card wwm-card wwm-defs'>
        {definitions.map((d) => (
          <React.Fragment key={d.term}>
            <dt>{d.term}</dt>
            <dd>{d.text}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
};
